/**
 * Offline drafts, on IndexedDB.
 *
 * A participant writing a bid or an agreement on a bus must not lose it when the signal drops. Anything typed while
 * offline is queued here and sent by `offlineSync` when the connection returns. Keyed by a stable draft key (e.g.
 * "bid:J151"), so saving the same draft twice replaces rather than duplicates: only the latest version is sent.
 *
 * Drafts only. Completion and scoring always happen live on the server.
 */
const DB_NAME = "gateway-offline";
const DB_VERSION = 1;
const DRAFTS = "drafts";

export interface PendingDraft {
  /** Stable key: the same value on every save of this draft. */
  id: string;
  /** Which sender replays it (see offlineSync). */
  kind: string;
  payload: unknown;
  queuedAt: number;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DRAFTS)) db.createObjectStore(DRAFTS, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB unavailable"));
  });
}

async function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(DRAFTS, mode);
    const request = work(transaction.objectStore(DRAFTS));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB write failed"));
    transaction.oncomplete = () => db.close();
  });
}

/** True when IndexedDB is usable at all: private mode can refuse it. */
export function offlineSupported(): boolean {
  return typeof indexedDB !== "undefined";
}

export const drafts = {
  async put(draft: PendingDraft): Promise<void> {
    await run("readwrite", (store) => store.put(draft));
  },
  async get(id: string): Promise<PendingDraft | undefined> {
    return run<PendingDraft | undefined>("readonly", (store) => store.get(id));
  },
  async all(): Promise<PendingDraft[]> {
    return run<PendingDraft[]>("readonly", (store) => store.getAll());
  },
  async remove(id: string): Promise<void> {
    await run("readwrite", (store) => store.delete(id));
  },
  async count(): Promise<number> {
    return run<number>("readonly", (store) => store.count());
  },
};
